import { Link, Popover, Stack, Typography } from "@mui/material";
import { WasteManifestSource } from "generated-graphql/graphql";

import { SOURCE_LABEL, SOURCE_ORDER } from "./manifestSourceDisplay";
import { SourceLetterBox } from "./SourceLetterBox";

export interface ProvenanceSourceValue {
  source: WasteManifestSource;
  /** Display value for this source, or null/undefined when the source has no value. */
  value: string | null | undefined;
}

export interface FieldProvenancePopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Field label shown as the popover title (e.g. "Quantity"). */
  fieldLabel: string;
  /** Per-source values; rows are ordered by SOURCE_ORDER regardless of input order. */
  values: ProvenanceSourceValue[];
  /** The currently-selected source (its row is bolded). */
  activeSource?: WasteManifestSource;
  /** Wires the "Open compare" link to the parent's DiscrepancyReviewDialog. */
  onOpenCompare?: () => void;
}

/**
 * Per-field provenance popover: lists all three source values inline (active
 * source bold), with an "Open compare" link into the full compare dialog.
 * Opened by clicking a `SourceLetterBox`.
 */
export function FieldProvenancePopover({
  open,
  anchorEl,
  onClose,
  fieldLabel,
  values,
  activeSource,
  onOpenCompare,
}: FieldProvenancePopoverProps) {
  const bySource = new Map(values.map((v) => [v.source, v.value]));

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Stack spacing={1} sx={{ p: 2, minWidth: 220, maxWidth: 320 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {fieldLabel}
        </Typography>
        <Stack spacing={0.75}>
          {SOURCE_ORDER.filter((source) => bySource.has(source)).map((source) => {
            const value = bySource.get(source);
            const isActive = source === activeSource;
            return (
              <Stack key={source} direction="row" spacing={1} alignItems="center">
                <SourceLetterBox source={source} size={15} tooltip={null} />
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
                  {SOURCE_LABEL[source]}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: isActive ? 600 : 400, wordBreak: "break-word" }}
                  color={value ? "text.primary" : "text.disabled"}
                >
                  {value || "Not in this source"}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        {onOpenCompare && (
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => {
              // Close this popover before opening the compare dialog so it
              // doesn't linger behind it.
              onClose();
              onOpenCompare();
            }}
            sx={{ alignSelf: "flex-start" }}
          >
            Open compare
          </Link>
        )}
      </Stack>
    </Popover>
  );
}
